/**
 * One ranked text: rank, the text (two lines) with its prompt group and
 * volume, why it is or is not reusable, the formula's values, the score with
 * its bar, "never reuse", and — opened — how the score is made with every
 * value. A text the catalog does not know is ranked but can never be reused.
 * The text rules' word (Phase 19) rides beside its status.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Archive, RotateCcw } from 'lucide-react';
import type { DropGenHourFormula, DropGenRankedText } from '@trafficpulse/shared';

import { Badge } from '@/components/ui';
import { cn } from '@/lib/utils';

import { excludeHit } from '../../../../rules/rule-model';
import { RuleHitBadges, RuleHitList } from '../../../../rules/RuleHits';
import { TagChips } from '../../../../rules/TagChips';
import type { RankingColumn } from '../ranking-columns';
import { rowGrid } from '../results-grid';
import { ScoreDetail } from '../ScoreDetail';
import { ExpandButton, MetricCells, ScoreCell } from './RowCells';

const SMS_MAX = 160;
const fmt = (n: number) => n.toLocaleString('en-US');

interface Props {
  t: DropGenRankedText;
  columns: RankingColumn[];
  formula: DropGenHourFormula;
  bar: number;
  open: boolean;
  onOpen: () => void;
  /** Absent on the example's made-up texts — nothing to exclude. */
  onToggleExclude?: () => void;
  /** "Not enough data": its number (null = no such rule), its range (the first one) and that range's words. */
  floor: { minVolume: number | null; rangeLabel: string; primaryRangeKey: string };
}

const Status: FC<{ t: DropGenRankedText }> = ({ t }) => {
  if (t.inPool) return <Badge tone="info">reuse pool</Badge>;
  if (t.excluded) return <Badge tone="secondary">never reused</Badge>;
  if (t.retired) return null; // the rule's own badge says it
  if (t.unranked) return excludeHit(t.ruleHits) ? null : <Badge tone="secondary">no data</Badge>; // the exclude rule's badge says it
  if (!t.text) return <Badge tone="secondary">not in the catalog</Badge>;
  if (t.text.length > SMS_MAX) return <Badge tone="secondary">longer than one SMS</Badge>;
  // The one reason left (Phase 19): a Proven rule is on and did not fire here.
  if (!t.reusable) return <Badge tone="secondary">not proven by a rule</Badge>;
  return null;
};

export const TextRow: FC<Props> = ({ t, columns, formula, bar, open, onOpen, onToggleExclude, floor }) => (
  <li className={cn(open && 'bg-primary/5')}>
    <div className={cn(rowGrid(columns.length), 'px-3 py-2')}>
      <span className="font-mono text-xs tabular-nums text-muted-foreground">{t.rank ? `#${t.rank}` : '—'}</span>
      <button type="button" onClick={onOpen} className="min-w-0 text-left">
        <p className={cn('line-clamp-2 text-sm', !t.text && 'italic text-muted-foreground')} title={t.text ?? undefined}>
          {t.text ?? `${t.templateId} — sent from outside TrafficPulse, its words are unknown here`}
        </p>
        <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
          <Status t={t} />
          <RuleHitBadges hits={t.ruleHits} />
          <TagChips tags={t.tags} />
          {/* The dot ends the group's span: when the line wraps, it never starts the next one. */}
          <span className="min-w-0 truncate">{t.groupName ?? 'no prompt group'} ·</span>
          <span className="whitespace-nowrap tabular-nums">{fmt(t.uniqueVisits)} unique visits · {fmt(t.conversions)} conv.</span>
        </p>
      </button>
      <MetricCells columns={columns} metricsByRange={t.metricsByRange} floor={floor} />
      <ScoreCell score={t.score} bar={bar} />
      <div className="flex items-center justify-end gap-0.5">
        {onToggleExclude && t.text && !t.unranked ? (
          <button
            type="button"
            onClick={onToggleExclude}
            title={t.excluded ? 'Allow this text to be reused again' : 'Never reuse this text (it stays ranked)'}
            aria-label={t.excluded ? 'Allow reuse' : 'Never reuse'}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            {t.excluded ? <RotateCcw className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
          </button>
        ) : null}
        <ExpandButton open={open} onClick={onOpen} what="this text" />
      </div>
    </div>
    {open && t.ruleHits?.length ? (
      <div className="px-3 pb-2">
        <RuleHitList hits={t.ruleHits} />
      </div>
    ) : null}
    {open ? (
      <ScoreDetail
        formula={formula}
        breakdown={t.breakdown}
        metricsByRange={t.metricsByRange}
        unrankedNote={`Excluded — ${excludeHit(t.ruleHits)?.reason ?? `no data in ${floor.rangeLabel}`}: not ranked yet, never counted as bad.`}
      />
    ) : null}
  </li>
);
